import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import api, { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("M");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`)
      .then(({ data }) => { setProduct(data); setSize(data.sizes?.[0] || "M"); })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <div className="max-w-[1400px] mx-auto px-6 py-40 font-serif text-4xl">Piece not found.</div>;
  if (!product) return <div className="max-w-[1400px] mx-auto px-6 py-40 font-mono text-xs uppercase tracking-[0.2em] text-[#595959]">Loading…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-24">
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#595959] hover:text-[#8C3A3A] transition-colors mb-10">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="grid md:grid-cols-12 gap-10 md:gap-16">
        <motion.div
          initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="md:col-span-7 overflow-hidden bg-[#F2EFE9] aspect-[4/5]"
        >
          <img src={product.images?.[0]} alt={product.name} className="w-full h-full object-cover" />
        </motion.div>

        <div className="md:col-span-5 md:pt-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C3A3A] mb-5">{product.category} · {product.fabric}</p>
          <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tight text-[#1A1A1A] leading-[0.95]">{product.name}</h1>
          <p data-testid="detail-price" className="font-mono text-xl text-[#1A1A1A] mt-6">{inr(product.price)}</p>
          <p className="font-sans text-[15px] text-[#595959] leading-relaxed mt-8">{product.description}</p>

          <div className="mt-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959] mb-4">Select Size</p>
            <div className="flex gap-2" data-testid="size-selector">
              {(product.sizes || []).map((s) => (
                <button
                  key={s}
                  data-testid={`size-${s}`}
                  onClick={() => setSize(s)}
                  className={`w-12 h-12 font-mono text-xs border transition-colors duration-300 ${
                    size === s ? "bg-[#1A1A1A] text-[#F9F8F6] border-[#1A1A1A]" : "border-[#E5E1D8] text-[#1A1A1A] hover:border-[#1A1A1A]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <button
            data-testid="add-to-cart-btn"
            onClick={() => addItem(product, size)}
            className="mt-10 w-full bg-[#1A1A1A] text-[#F9F8F6] py-5 font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-[#8C3A3A] transition-colors duration-300"
          >
            Add to Bag — {inr(product.price)}
          </button>
          <Link to="/cart" className="mt-3 block text-center font-mono text-[11px] uppercase tracking-[0.18em] text-[#595959] hover:text-[#8C3A3A] transition-colors py-2">Go to Bag</Link>

          <div className="mt-12 border-t border-[#E5E1D8] pt-8 space-y-4">
            {[[Truck, "Free shipping across India"], [RotateCcw, "15-day easy refunds"], [ShieldCheck, "Secure payments via Razorpay"]].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-3 text-[#595959]">
                <Icon size={16} className="text-[#8C3A3A]" />
                <span className="font-sans text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
